import React, { FC, useCallback, useEffect } from "react";

import { createFolderBusinessFilter } from "@keepeek/commons";
import {
  KMenuElementPosition,
  KMenuLinkType,
  KMenuWidget as KMenuWidgetComponent,
  LinkMenu,
  useThemeColors,
} from "@keepeek/refront-components";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";

import {
  KMenuWidget,
  Link,
  LinksByLocale,
  LinkType,
} from "../../../models/configuration/components/kMenuWidget";
import { useActiveFiltersFromURL } from "../../../providers/businessFilter/hooks/useActiveFiltersFromURL";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";
import { ComponentNextLink } from "../utils/parseInternalUrl";

export type MenuWidgetProps = {
  widgetId: KMenuWidget["id"];
  linksByLocale?: LinksByLocale[];
  container?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  color?: string;
  menuColorOnHover?: string;
  menuHeight?: string;
  linksPosition: KMenuElementPosition;
  horizontalLinksPadding?: string;
  linksSize?: string;
} & KWidgetProps;

const MenuWidget: FC<React.PropsWithChildren<MenuWidgetProps>> = function ({
  widgetId,
  linksByLocale,
  container,
  backgroundColor,
  borderColor,
  color,
  menuColorOnHover,
  menuHeight,
  linksPosition,
  horizontalLinksPadding,
  linksSize,
  type,
}) {
  const { setWidgetState, widgetState } = useWidgetState({
    id: widgetId,
    type,
  });
  const { locale: currentLocale } = useRouter();
  const { t } = useTranslation();
  const { convertColor } = useThemeColors();
  const { setActiveFilters } = useActiveFiltersFromURL();

  const getMenuLinksForCurrentLocaleIfPresents = useCallback(
    (_linksByLocale: any) => {
      const menuLinks: Array<LinkMenu> = [];
      const linksForCurrentLocale = _linksByLocale?.find(
        (lbl) => currentLocale?.toLocaleLowerCase() === lbl.locale?.toLocaleLowerCase(),
      );
      linksForCurrentLocale?.links?.forEach((l: Link) => {
        menuLinks.push({
          label: l.label,
          type:
            l.linkType === LinkType.URL
              ? (LinkType.URL as unknown as KMenuLinkType)
              : (LinkType.Folder as unknown as KMenuLinkType),
          href: l.href || "",
          openInNewTab: l.openInNewTab || false,
          folderId: l.folderId || 0,
        } as LinkMenu);
      });
      return menuLinks;
    },
    [currentLocale],
  );

  const handleFolderClick = (element: LinkMenu) => {
    if (element.type === KMenuLinkType.FOLDER && element.folderId) {
      const folderId: number = element.folderId;
      setActiveFilters([
        createFolderBusinessFilter(
          folderId,
          element.label || "",
          t("business-filter.advanced.folder.label"),
        ),
      ]);
    }
  };

  useEffect(() => {
    if (!getMenuLinksForCurrentLocaleIfPresents(linksByLocale).length) {
      setWidgetState({ state: WidgetUIState.NULL });
    } else {
      setWidgetState({ state: WidgetUIState.RENDERED });
    }
  }, [getMenuLinksForCurrentLocaleIfPresents, linksByLocale, setWidgetState]);

  if (widgetState.state === WidgetUIState.RENDERED) {
    return (
      <KMenuWidgetComponent
        sx={{ flex: 1 }}
        links={getMenuLinksForCurrentLocaleIfPresents(linksByLocale)}
        container={container}
        backgroundColor={convertColor(backgroundColor)}
        borderColor={convertColor(borderColor)}
        color={convertColor(color)}
        menuColorOnHover={convertColor(menuColorOnHover)}
        horizontalLinksPadding={horizontalLinksPadding}
        linksPosition={linksPosition}
        menuHeight={menuHeight}
        linksSize={linksSize}
        handleFolderClick={handleFolderClick}
        ComponentNextLinkCallBack={ComponentNextLink}
      />
    );
  }
  return null;
};

export default MenuWidget;
